import { visitorStatus } from "./constants";
import {
  getClosingStatusBadgeClass,
  normalizeFollowupClosingStatus,
} from "./appointmentListStatusDisplay";

/** App visitor listing labels (lead.lead_status). */
export const APP_LEAD_STATUS_LABELS = {
  1: "New Lead",
  2: "Follow-up",
  3: "Ready to Visit",
  4: "Booking",
  5: "Registration",
  6: "Not interested",
  7: "Ready To Book",
  8: "Cancel visit",
};

export function getLeadStatusLabel(leadStatus) {
  const code = Number(leadStatus);
  if (APP_LEAD_STATUS_LABELS[code]) return APP_LEAD_STATUS_LABELS[code];
  return visitorStatus[code] || "";
}

const getLatestFollowupTitle = (item = {}) =>
  String(
    item?.call_status ||
      item?.last_followup_title ||
      item?.lastFollowupTitle ||
      "",
  ).trim();

/** CM allocation / reallocation audit titles must not drive visitor status. */
const isCmAllocationFollowupTitle = (value = "") => {
  const text = String(value || "").trim();
  if (!text) return false;
  if (text.startsWith("(CM)") || text.startsWith("[CM Assignment]")) {
    return true;
  }
  const normalized = text.toLowerCase().replace(/\s+/g, " ");
  return (
    /re-allocat/.test(normalized) ||
    /assign appointment/.test(normalized) ||
    /assign to (new )?cm/.test(normalized)
  );
};

const collectAppointmentStatusCodes = (raw) => {
  const codes = [];
  const push = (value) => {
    const n = Number(value);
    if (Number.isFinite(n) && n > 0) codes.push(n);
  };
  if (Array.isArray(raw)) {
    raw.forEach((value) => {
      if (value && typeof value === "object" && "status" in value) {
        push(value.status);
      } else {
        push(value);
      }
    });
    return codes;
  }
  push(raw);
  return codes;
};

/**
 * Visitor Status: latest follow-up/closing title (Call / Follow-Up) wins over
 * frozen lead_status "Ready to visit".
 */
export function resolveVisitorDisplayStatus(item = {}) {
  const leadCode = Number(item?.lead_status);
  if ([4, 5, 6, 8].includes(leadCode)) {
    return {
      label: getLeadStatusLabel(leadCode),
      badgeKey: leadCode,
      source: "lead",
    };
  }

  let followupTitle = getLatestFollowupTitle(item);
  const followupNorm = followupTitle.toLowerCase().replace(/\s+/g, " ");
  if (
    followupNorm.includes("revisit") &&
    (followupNorm.includes("check") || followupNorm.includes("visited"))
  ) {
    return {
      label: "Revisit - checked in",
      badgeKey: 2,
      source: "followup",
    };
  }
  if (
    followupNorm === "lead create" ||
    /check/.test(followupNorm) ||
    isCmAllocationFollowupTitle(followupTitle)
  ) {
    followupTitle = "";
  }

  if (followupTitle) {
    const normalized = followupTitle.toLowerCase().replace(/\s+/g, " ");
    if (normalizeFollowupClosingStatus(followupTitle) === "Follow-Up") {
      return { label: "Follow-up", badgeKey: 10, source: "followup" };
    }
    if (normalized.includes("revisit")) {
      return {
        label: "Upcoming Revisit",
        badgeKey: 2,
        source: "followup",
      };
    }
    if (normalized.includes("not interest") || normalized.includes("not fit")) {
      return { label: "Not interested", badgeKey: 6, source: "followup" };
    }
    if (normalized.includes("cancel")) {
      return { label: "Cancel visit", badgeKey: 8, source: "followup" };
    }
    if (normalized.includes("book")) {
      return { label: "Booking", badgeKey: 4, source: "followup" };
    }
    if (normalized.includes("reschedule")) {
      return { label: "Reschedule", badgeKey: 5, source: "followup" };
    }
  }

  const appointmentCodes = collectAppointmentStatusCodes(
    item?.appointment_status,
  );
  if (appointmentCodes.includes(10)) {
    return { label: "Follow-up", badgeKey: 10, source: "followup" };
  }

  if (leadCode && getLeadStatusLabel(leadCode)) {
    return {
      label: getLeadStatusLabel(leadCode),
      badgeKey: leadCode,
      source: "lead",
    };
  }

  return { label: "", badgeKey: null, source: null };
}

export function getVisitorDisplayBadgeClass(resolved) {
  if (!resolved?.label) return "visitor-status-badge--default";
  return getClosingStatusBadgeClass(resolved.label);
}
